import { Collection, getFirebase } from 'firestorter';

import BaseDocument from '../../Model/BaseDocument';
import CollectionName from '../../utils/collections';

/**
 * Enum for the lifecycle status of a UserActionItem.
 * @enum {string}
 */
const ActionItemStatus = {
  PENDING: 'PENDING',
  READ: 'READ',
  RESOLVED: 'RESOLVED',
};

const ActionItemType = {
  LEAD_COMMENT: 'LEAD_COMMENT',
  LEAD_REPLY: 'LEAD_REPLY',
};

/**
 * Represents a userActionItems document — a notification created when a user
 * is @mentioned in a lead comment. Stored as a flat collection at
 * `userActionItems/{itemId}` with `userId` as a field.
 *
 * Status lifecycle:
 *   PENDING → READ      when the user opens the item in the drawer
 *   PENDING → RESOLVED  when the user dismisses the item (X button)
 *   READ    → RESOLVED  when the user dismisses a previously-read item
 *
 * RESOLVED items are excluded from the Firestore query and never surfaced in
 * the drawer. Badge count reflects PENDING items only.
 *
 * @class UserActionItem
 * @extends BaseDocument
 */
class UserActionItem extends BaseDocument {
  constructor(id, opts) {
    super(`${CollectionName.USER_ACTION_ITEMS}/${id}`, opts);
  }

  /**
   * ID of the user this action item belongs to.
   * @returns {string}
   */
  get userId() {
    return this.data.userId || '';
  }

  /**
   * Current lifecycle status of this action item.
   * @returns {string} One of ActionItemStatus.PENDING | READ | RESOLVED
   */
  get status() {
    return this.data.status || ActionItemStatus.PENDING;
  }

  /**
   * Type of the action item.
   * @returns {string}
   */
  get type() {
    return this.data.type;
  }

  /**
   * ID of the lead this action item is associated with.
   * Used to open the lead modal when the user acts on the item.
   * @returns {string}
   */
  get leadId() {
    return this.data.leadId || '';
  }

  /**
   * Display name of the lead, denormalized at write time to avoid an extra
   * Firestore read when rendering the notifications drawer.
   * @returns {string}
   */
  get leadName() {
    return this.data.leadName || '';
  }

  /**
   * ID of the user who triggered the action item.
   * @returns {string}
   */
  get triggeredBy() {
    return this.data.triggeredBy || '';
  }

  /**
   * Display name of the user who wrote the comment containing the @mention.
   * @returns {string}
   */
  get triggerUserName() {
    return this.data.triggerUserName || '';
  }

  /**
   * Raw text of the comment that triggered this action item.
   * @returns {string}
   */
  get message() {
    return this.data.message || '';
  }

  /**
   * Timestamp when this action item was created.
   * @returns {Date|null}
   */
  get createdAt() {
    return this.data.createdAt?.toDate() || null;
  }

  /**
   * Mark this item as READ. Called when the user opens the item in the drawer.
   * The item remains visible but is visually dimmed and no longer counted in
   * the badge.
   * @returns {Promise<void>}
   */
  async markAsRead() {
    return this.update({ status: ActionItemStatus.READ });
  }

  /**
   * Mark this item as RESOLVED. Called when the user dismisses the item (X button).
   * Resolved items are excluded from the Firestore query and disappear from
   * the drawer.
   * @returns {Promise<void>}
   */
  async resolve() {
    return this.update({
      status: ActionItemStatus.RESOLVED,
      resolvedAt: getFirebase().firestore.FieldValue.serverTimestamp(),
    });
  }

  /**
   * Returns a live firestorter Collection of action items for the given user,
   * filtered to PENDING and READ items only, ordered newest-first.
   * The caller is responsible for reacting to collection changes via MobX autorun.
   *
   * @param {string} userId - The authenticated user's ID.
   * @returns {Promise<Collection>} A fetched firestorter Collection of UserActionItem documents.
   */
  static async getItemsForUser(userId) {
    const collection = new Collection(CollectionName.USER_ACTION_ITEMS, {
      createDocument: (src, opts) => new UserActionItem(src.id, opts),
      query: (ref) => ref
        .where('userId', '==', userId)
        .where('status', 'in', [ActionItemStatus.PENDING, ActionItemStatus.READ])
        .orderBy('createdAt', 'desc'),
    });
    await collection.fetch();
    return collection;
  }
}

export default UserActionItem;
export { ActionItemStatus, ActionItemType };
